// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import { toErr } from '@easyflow/shared';
import {
  FileSignature, Receipt, CreditCard, Plus, Edit, Trash2, Copy, Eye, Save, CheckCircle,
  XCircle, RefreshCw, Tag, ExternalLink, Info, User
} from 'lucide-react';
import api from '@/lib/api';
import { CRM_BASE } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterProductCategory as ProductCategory,
  CrmMasterContractTemplate as ContractTemplate,
  CrmMasterFicSyncLog as FicSyncLog,
  CrmMasterStripeConfig as StripeConfigData,
  CrmMasterFicConfig as FicConfigData,
  CrmMasterVoipTariff as VoipTariff,
} from '@easyflow/shared';
import {
  Tip, InputField, Badge, ModalWrapper
} from './shared';

export function TabImpostazioni() {
  // Il GET non ritorna mai i segreti (solo hint): i campi in chiaro esistono solo nel draft locale pre-salvataggio.
  const [stripeConfig, setStripeConfig] = useState<StripeConfigData & { secret_key?: string; webhook_secret?: string }>({});
  const [ficConfig, setFicConfig] = useState<FicConfigData & { client_secret?: string }>({});
  const [syncLogs, setSyncLogs] = useState<FicSyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingStripe, setSavingStripe] = useState(false);
  const [savingFic, setSavingFic] = useState(false);
  const [contractTemplates, setContractTemplates] = useState<ContractTemplate[]>([]);
  const [editTemplate, setEditTemplate] = useState<ContractTemplate | null>(null);
  const [voipTariffs, setVoipTariffs] = useState<VoipTariff[]>([]);
  // Partial: la "Nuova Tariffa" parte come draft senza id/timestamps.
  const [editVoipTariff, setEditVoipTariff] = useState<Partial<VoipTariff> | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState<ContractTemplate | null>(null);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [editCategory, setEditCategory] = useState<ProductCategory | null>(null);
  const [savingCategory, setSavingCategory] = useState(false);

  const categoryColors = ['blue', 'green', 'red', 'amber', 'purple', 'pink', 'cyan', 'orange'];

  const loadCategories = async () => {
    try {
      const res = await api.get<ProductCategory[]>(`${CRM_BASE}/product-categories`);
      if (res.success && res.data) setProductCategories(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const saveCategory = async () => {
    if (!editCategory?.name) return;
    setSavingCategory(true);
    try {
      if (editCategory.id) {
        await api.put(`${CRM_BASE}/product-categories/${editCategory.id}`, editCategory);
      } else {
        await api.post(`${CRM_BASE}/product-categories`, editCategory);
      }
      setEditCategory(null);
      await loadCategories();
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCategory = async (id: string) => {
    if (!confirm('Eliminare questa categoria?')) return;
    try {
      await api.delete(`${CRM_BASE}/product-categories/${id}`);
      await loadCategories();
    } catch {}
  };

  useEffect(() => {
    const load = async () => {
      const results = await Promise.allSettled([
        api.get<StripeConfigData>(`${CRM_BASE}/stripe/config`),
        api.get<FicConfigData>(`${CRM_BASE}/fic/config`),
        api.get<FicSyncLog[] | { logs?: FicSyncLog[] }>(`${CRM_BASE}/fic/sync-logs`),
        api.get<ContractTemplate[]>(`${CRM_BASE}/contract-templates`),
        api.get<ProductCategory[]>(`${CRM_BASE}/product-categories`),
        api.get<VoipTariff[]>(`${CRM_BASE}/voip-tariffs`),
      ]);
      const [stripeRes, ficRes, logsRes, templatesRes, catRes, voipRes] = results;
      if (voipRes.status === 'fulfilled' && voipRes.value?.success && voipRes.value.data) setVoipTariffs(Array.isArray(voipRes.value.data) ? voipRes.value.data : []);
      if (stripeRes.status === 'fulfilled' && stripeRes.value?.success && stripeRes.value.data) setStripeConfig(stripeRes.value.data);
      if (ficRes.status === 'fulfilled' && ficRes.value?.success && ficRes.value.data) setFicConfig(ficRes.value.data);
      if (logsRes.status === 'fulfilled' && logsRes.value?.success && logsRes.value.data) setSyncLogs(Array.isArray(logsRes.value.data) ? logsRes.value.data : (logsRes.value.data).logs || []);
      if (templatesRes.status === 'fulfilled' && templatesRes.value?.success && templatesRes.value.data) setContractTemplates(Array.isArray(templatesRes.value.data) ? templatesRes.value.data : []);
      if (catRes.status === 'fulfilled' && catRes.value?.success && catRes.value.data) setProductCategories(Array.isArray(catRes.value.data) ? catRes.value.data : []);
      setLoading(false);
    };
    void load();
  }, []);

  const saveStripe = async () => {
    setSavingStripe(true);
    try {
      await api.put(`${CRM_BASE}/stripe/config`, stripeConfig);
    } finally {
      setSavingStripe(false);
    }
  };

  const saveFic = async () => {
    setSavingFic(true);
    try {
      await api.put(`${CRM_BASE}/fic/config`, ficConfig);
    } finally {
      setSavingFic(false);
    }
  };

  const updateStripe = (field: string) => (value: string | number | boolean) => setStripeConfig((c) => ({ ...c, [field]: value }));
  const updateFic = (field: string) => (value: string | number | boolean) => setFicConfig((c) => ({ ...c, [field]: value }));

  const saveTemplate = async () => {
    if (!editTemplate?.name || !editTemplate?.html_content) return;
    setSavingTemplate(true);
    try {
      const res = await api.put(`${CRM_BASE}/contract-templates`, editTemplate);
      if (res.success) {
        setEditTemplate(null);
        const templatesRes = await api.get<ContractTemplate[]>(`${CRM_BASE}/contract-templates`);
        if (templatesRes.success && templatesRes.data) setContractTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      }
    } finally {
      setSavingTemplate(false);
    }
  };

  if (loading) return <div className="text-muted-foreground dark:text-muted-foreground/70 p-8">Caricamento configurazioni...</div>;

  const webhookUrl = typeof window !== 'undefined' ? `${window.location.origin}/api/master/crm/stripe/webhook` : '';

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground dark:text-white font-semibold flex items-center gap-2"><CreditCard className="h-5 w-5" /> Configurazione Stripe</h3>
          <div className="flex items-center gap-2">
            {stripeConfig.is_connected ? (
              <span className="flex items-center gap-1 text-green-400 text-sm"><CheckCircle className="h-4 w-4" /> Connesso</span>
            ) : (
              <span className="flex items-center gap-1 text-muted-foreground dark:text-muted-foreground/70 text-sm"><XCircle className="h-4 w-4" /> Non connesso</span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField label="Secret Key" value={stripeConfig.secret_key || ''} onChange={updateStripe('secret_key')} type="password" placeholder={stripeConfig.secret_key_hint || 'sk_...'} />
          <InputField label="Publishable Key" value={stripeConfig.publishable_key || ''} onChange={updateStripe('publishable_key')} placeholder="pk_..." />
          <InputField label="Webhook Secret" value={stripeConfig.webhook_secret || ''} onChange={updateStripe('webhook_secret')} type="password" placeholder={stripeConfig.webhook_secret_hint || 'whsec_...'} />
          <div>
            <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Modalità</label>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={() => updateStripe('is_live_mode')(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${!stripeConfig.is_live_mode ? 'bg-amber-600 text-foreground dark:text-white' : 'bg-gray-200 dark:bg-gray-700 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white'}`}>
                Test
              </button>
              <button onClick={() => updateStripe('is_live_mode')(true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${stripeConfig.is_live_mode ? 'bg-green-600 text-foreground dark:text-white' : 'bg-gray-200 dark:bg-gray-700 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white'}`}>
                Live
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Webhook URL</label>
          <div className="flex items-center gap-2">
            <input value={webhookUrl} readOnly
              className="flex-1 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-muted-foreground dark:text-muted-foreground/60 rounded-lg px-3 py-2 text-sm" />
            <button onClick={() => navigator.clipboard.writeText(webhookUrl)}
              className="bg-muted dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-muted-foreground dark:text-muted-foreground/60 px-3 py-2 rounded-lg text-sm transition-colors">
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={saveStripe} disabled={savingStripe}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-foreground dark:text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50">
            <Save className="h-4 w-4" />{savingStripe ? 'Salvataggio...' : 'Salva Stripe'}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground dark:text-white font-semibold flex items-center gap-2"><Receipt className="h-5 w-5" /> Fatture in Cloud</h3>
          <div className="flex items-center gap-2">
            {ficConfig.is_connected ? (
              <span className="flex items-center gap-1 text-green-400 text-sm"><CheckCircle className="h-4 w-4" /> Connesso</span>
            ) : (
              <span className="flex items-center gap-1 text-muted-foreground dark:text-muted-foreground/70 text-sm"><XCircle className="h-4 w-4" /> Non connesso</span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField label="Client ID" value={ficConfig.client_id || ''} onChange={updateFic('client_id')} placeholder="Client ID..." />
          <InputField label="Client Secret" value={ficConfig.client_secret || ''} onChange={updateFic('client_secret')} type="password" placeholder={ficConfig.client_secret_hint || 'Client Secret...'} />
          <InputField label="Company ID" value={ficConfig.company_id || ''} onChange={updateFic('company_id')} placeholder="Rilevato automaticamente dopo OAuth" />
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={saveFic} disabled={savingFic}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-foreground dark:text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50">
            <Save className="h-4 w-4" />{savingFic ? 'Salvataggio...' : 'Salva FiC'}
          </button>
          {ficConfig.client_id && !ficConfig.is_connected && (
            <button onClick={async () => {
              try {
                const res = await api.post<{ url?: string }>(`${CRM_BASE}/fic/connect`, {});
                const resData = res.data;
                if (res.success && resData?.url) {
                  window.location.href = resData.url;
                }
              } catch (e: unknown) {
                const __e = toErr(e);
                alert('Errore: ' + (__e.message || 'impossibile avviare OAuth'));
              }
            }}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
              <ExternalLink className="h-4 w-4" /> Connetti OAuth
            </button>
          )}
          {ficConfig.is_connected && (
            <button onClick={async () => {
              try {
                const res = await api.post<{ url?: string }>(`${CRM_BASE}/fic/connect`, {});
                const resData = res.data;
                if (res.success && resData?.url) {
                  window.location.href = resData.url;
                }
              } catch (e: unknown) {
                const __e = toErr(e);
                alert('Errore: ' + (__e.message || 'impossibile avviare OAuth'));
              }
            }}
              className="flex items-center gap-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-foreground dark:text-muted-foreground/60 px-4 py-2 rounded-lg font-medium transition-colors text-sm">
              <RefreshCw className="h-4 w-4" /> Riconnetti
            </button>
          )}
        </div>
      </div>

      {syncLogs.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
          <h3 className="text-foreground dark:text-white font-semibold mb-4">Log Sincronizzazione FiC</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border dark:border-gray-700">
                  <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Tipo</th>
                  <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Azione</th>
                  <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Stato</th>
                  <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Errore</th>
                  <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Data</th>
                </tr>
              </thead>
              <tbody>
                {syncLogs.slice(0, 20).map((log) => (
                  <tr key={log.id} className="border-b border-border dark:border-gray-700/50">
                    <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/60 text-sm">{log.entity_type}</td>
                    <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/60 text-sm">{log.action}</td>
                    <td className="py-2 px-3">
                      <Badge className={log.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>{log.status}</Badge>
                    </td>
                    <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm truncate max-w-xs">{log.error_message || '-'}</td>
                    <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">{new Date(log.synced_at ?? '').toLocaleString('it-IT')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground dark:text-white font-semibold flex items-center gap-2"><FileSignature className="h-5 w-5" /> Template Contratti</h3>
          <button onClick={() => setEditTemplate({ name: '', description: '', html_content: '', is_default: false } as ContractTemplate)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
            <Plus className="h-4 w-4" /> Nuovo Template
          </button>
        </div>
        <div className="space-y-3">
          {contractTemplates.map((t) => (
            <div key={t.id} className="bg-muted dark:bg-gray-700/50 rounded-lg p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-foreground dark:text-white font-medium">{t.name}</span>
                  {t.is_default && <Badge className="bg-blue-500/20 text-blue-400">Default</Badge>}
                </div>
                {t.description && <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm mt-1">{t.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Tip label="Anteprima"><button onClick={() => setShowTemplatePreview(t)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white p-1"><Eye className="h-4 w-4" /></button></Tip>
                <Tip label="Modifica"><button onClick={() => setEditTemplate({ ...t })} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white p-1"><Edit className="h-4 w-4" /></button></Tip>
                <Tip label="Duplica"><button onClick={async () => { await api.post(`${CRM_BASE}/contract-templates/${t.id}/duplicate`); const r = await api.get<ContractTemplate[]>(`${CRM_BASE}/contract-templates`); if (r.success && r.data) setContractTemplates(Array.isArray(r.data) ? r.data : []); }} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-blue-400 p-1"><Copy className="h-4 w-4" /></button></Tip>
                <Tip label="Elimina"><button onClick={async () => { if (!confirm('Eliminare questo template?')) return; await api.delete(`${CRM_BASE}/contract-templates/${t.id}`); const r = await api.get<ContractTemplate[]>(`${CRM_BASE}/contract-templates`); if (r.success && r.data) setContractTemplates(Array.isArray(r.data) ? r.data : []); }} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button></Tip>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-foreground dark:text-white font-semibold flex items-center gap-2">📞 Tariffe VoIP</h3>
            <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-1">Listino €/min usato dal bundle EdgVoIP. Snapshot al momento della creazione contratto.</p>
          </div>
          <button onClick={() => setEditVoipTariff({ tariff_key: '', label: '', price_per_min: 0, sort_order: (voipTariffs.reduce((m, t) => Math.max(m, t.sort_order || 0), 0) + 10), is_active: true })}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium"><Plus className="h-4 w-4" /> Nuova Tariffa</button>
        </div>
        <div className="overflow-hidden rounded-lg border border-border dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-muted dark:bg-gray-700/50 text-muted-foreground dark:text-muted-foreground/60">
              <tr><th className="text-left px-3 py-2">Chiave</th><th className="text-left px-3 py-2">Etichetta</th><th className="text-right px-3 py-2">Prezzo</th><th className="text-left px-3 py-2">Unità</th><th className="text-center px-3 py-2">Ordine</th><th className="text-center px-3 py-2">Attiva</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {voipTariffs.length === 0 ? <tr><td colSpan={7} className="px-3 py-3 text-center text-muted-foreground dark:text-muted-foreground/70">Nessuna tariffa</td></tr> : voipTariffs.map((t) => (
                <tr key={t.id} className="border-t border-border dark:border-gray-700">
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground dark:text-muted-foreground/60">{t.tariff_key}</td>
                  <td className="px-3 py-2 text-foreground dark:text-white">{t.label}</td>
                  <td className="px-3 py-2 text-right text-foreground dark:text-white">{Number(t.price_per_min).toFixed(4).replace('.', ',')}</td>
                  <td className="px-3 py-2 text-muted-foreground dark:text-muted-foreground/70 text-xs">{t.unit_label || '€/min'}</td>
                  <td className="px-3 py-2 text-center text-muted-foreground dark:text-muted-foreground/70">{t.sort_order}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={async () => { await api.patch(`${CRM_BASE}/voip-tariffs/${t.id}/toggle`, {}); const r = await api.get<VoipTariff[]>(`${CRM_BASE}/voip-tariffs`); if (r.success && r.data) setVoipTariffs(Array.isArray(r.data) ? r.data : []); }} title="Attiva/Disattiva">
                      {t.is_active ? <CheckCircle className="h-4 w-4 text-green-500 inline" /> : <span className="text-muted-foreground/70">—</span>}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditVoipTariff({ ...t })} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-blue-500 p-1"><Edit className="h-4 w-4" /></button>
                    <button onClick={async () => { if (!confirm(`Eliminare tariffa "${t.label}"?`)) return; await api.delete(`${CRM_BASE}/voip-tariffs/${t.id}`); const r = await api.get<VoipTariff[]>(`${CRM_BASE}/voip-tariffs`); if (r.success && r.data) setVoipTariffs(Array.isArray(r.data) ? r.data : []); }} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-500 p-1 ml-1"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ModalWrapper open={!!editVoipTariff} onClose={() => setEditVoipTariff(null)} title={editVoipTariff?.id ? 'Modifica Tariffa VoIP' : 'Nuova Tariffa VoIP'}>
        {editVoipTariff && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Chiave (es. fixed_national)</label>
              <input type="text" value={editVoipTariff.tariff_key} onChange={(e) => setEditVoipTariff({ ...editVoipTariff, tariff_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                disabled={!!editVoipTariff.id}
                className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-3 py-2 text-sm font-mono disabled:opacity-60" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Etichetta</label>
              <input type="text" value={editVoipTariff.label} onChange={(e) => setEditVoipTariff({ ...editVoipTariff, label: e.target.value })}
                className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Placeholder template (es. tariff_fixed_national)</label>
              <input type="text" value={editVoipTariff.placeholder_key || ''} placeholder={`tariff_${editVoipTariff.tariff_key || ''}`}
                onChange={(e) => setEditVoipTariff({ ...editVoipTariff, placeholder_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-3 py-2 text-sm font-mono" />
              <p className="text-[10px] text-muted-foreground dark:text-muted-foreground/70 mt-1">Userà {`{{${editVoipTariff.placeholder_key || ('tariff_' + (editVoipTariff.tariff_key || ''))}}}`} nel template HTML.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Prezzo</label>
                <input type="number" step="0.0001" min="0" value={editVoipTariff.price_per_min} onChange={(e) => setEditVoipTariff({ ...editVoipTariff, price_per_min: parseFloat(e.target.value || '0') })}
                  className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Unità</label>
                <input type="text" maxLength={20} value={editVoipTariff.unit_label || '€/min'} onChange={(e) => setEditVoipTariff({ ...editVoipTariff, unit_label: e.target.value })}
                  className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Ordine</label>
                <input type="number" value={editVoipTariff.sort_order ?? 0} onChange={(e) => setEditVoipTariff({ ...editVoipTariff, sort_order: parseInt(e.target.value || '0') })}
                  className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-3 py-2 text-sm" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground dark:text-muted-foreground/60">
              <input type="checkbox" checked={editVoipTariff.is_active !== false} onChange={(e) => setEditVoipTariff({ ...editVoipTariff, is_active: e.target.checked })} />
              Tariffa attiva
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditVoipTariff(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/60">Annulla</button>
              <button onClick={async () => {
                try {
                  const res = await api.put(`${CRM_BASE}/voip-tariffs`, editVoipTariff);
                  if (!res.success) throw new Error(res.error || 'Errore salvataggio');
                  setEditVoipTariff(null);
                  const r = await api.get<VoipTariff[]>(`${CRM_BASE}/voip-tariffs`);
                  if (r.success && r.data) setVoipTariffs(Array.isArray(r.data) ? r.data : []);
                } catch (e: unknown) {
                  const __e = toErr(e); alert(__e.message || 'Errore'); }
              }} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium">Salva</button>
            </div>
          </div>
        )}
      </ModalWrapper>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-foreground dark:text-white font-semibold flex items-center gap-2 mb-4"><Info className="h-5 w-5" /> Variabili Disponibili nei Template</h3>
        <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm mb-4">Usa queste variabili nei template contratto. Verranno sostituite automaticamente con i dati reali al momento della creazione.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <h4 className="text-sm font-semibold text-blue-500 mb-2">Azienda (Fornitore)</h4>
            <div className="space-y-1 text-xs text-muted-foreground dark:text-muted-foreground/70">
              <p><code className="text-blue-400">{'{{company_name}}'}</code> - Ragione sociale</p>
              <p><code className="text-blue-400">{'{{company_vat}}'}</code> - Partita IVA</p>
              <p><code className="text-blue-400">{'{{company_cf}}'}</code> - Codice fiscale</p>
              <p><code className="text-blue-400">{'{{company_address}}'}</code> - Indirizzo</p>
              <p><code className="text-blue-400">{'{{company_zip}}'}</code> / <code className="text-blue-400">{'{{company_city}}'}</code> / <code className="text-blue-400">{'{{company_province}}'}</code></p>
              <p><code className="text-blue-400">{'{{company_representative}}'}</code> - Legale rappresentante</p>
              <p><code className="text-blue-400">{'{{master_pec}}'}</code> - PEC azienda</p>
              <p><code className="text-blue-400">{'{{master_phone}}'}</code> / <code className="text-blue-400">{'{{master_email}}'}</code></p>
              <p><code className="text-blue-400">{'{{master_sdi}}'}</code> - Codice SDI</p>
              <p><code className="text-blue-400">{'{{master_iban}}'}</code> - IBAN</p>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-green-500 mb-2">Cliente</h4>
            <div className="space-y-1 text-xs text-muted-foreground dark:text-muted-foreground/70">
              <p><code className="text-green-400">{'{{client_name}}'}</code> - Ragione sociale</p>
              <p><code className="text-green-400">{'{{client_vat}}'}</code> - Partita IVA</p>
              <p><code className="text-green-400">{'{{client_fiscal_code}}'}</code> - Codice fiscale</p>
              <p><code className="text-green-400">{'{{client_representative}}'}</code> - Rappresentante legale</p>
              <p><code className="text-green-400">{'{{client_address}}'}</code> / <code className="text-green-400">{'{{client_zip}}'}</code> / <code className="text-green-400">{'{{client_city}}'}</code> / <code className="text-green-400">{'{{client_province}}'}</code></p>
              <p><code className="text-green-400">{'{{client_pec}}'}</code> / <code className="text-green-400">{'{{client_sdi}}'}</code> - Codice SDI</p>
              <p><code className="text-green-400">{'{{client_email}}'}</code> / <code className="text-green-400">{'{{client_phone}}'}</code></p>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-amber-500 mb-2">Prodotti e Totali</h4>
            <div className="space-y-1 text-xs text-muted-foreground dark:text-muted-foreground/70">
              <p><code className="text-amber-400">{'{{items_rows}}'}</code> - Righe tabella HTML (desc, qtà, prezzo, IVA%, totale)</p>
              <p><code className="text-amber-400">{'{{subtotal_net}}'}</code> / <code className="text-amber-400">{'{{total_net}}'}</code> - Subtotale netto</p>
              <p><code className="text-amber-400">{'{{total_vat}}'}</code> - Totale IVA</p>
              <p><code className="text-amber-400">{'{{total_gross}}'}</code> - Totale lordo</p>
            </div>
            <h4 className="text-sm font-semibold text-purple-500 mb-2 mt-3">Pagamento</h4>
            <div className="space-y-1 text-xs text-muted-foreground dark:text-muted-foreground/70">
              <p><code className="text-purple-400">{'{{payment_formula}}'}</code> - Formula (unica soluz. / rate)</p>
              <p><code className="text-purple-400">{'{{entry_fee}}'}</code> - Entry fee</p>
              <p><code className="text-purple-400">{'{{installments_count}}'}</code> / <code className="text-purple-400">{'{{first_installment_amount}}'}</code></p>
              <p><code className="text-purple-400">{'{{installment_plan_table}}'}</code> - Tabella piano rateale</p>
              <p><code className="text-purple-400">{'{{payment_terms}}'}</code> - Condizioni pagamento</p>
            </div>
            <h4 className="text-sm font-semibold text-red-500 mb-2 mt-3">Contratto</h4>
            <div className="space-y-1 text-xs text-muted-foreground dark:text-muted-foreground/70">
              <p><code className="text-red-400">{'{{contract_number}}'}</code> / <code className="text-red-400">{'{{contract_date}}'}</code></p>
              <p><code className="text-red-400">{'{{contract_start_date}}'}</code> / <code className="text-red-400">{'{{contract_end_date}}'}</code></p>
              <p><code className="text-red-400">{'{{contract_duration}}'}</code> - Durata (es. "12 mesi")</p>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-border dark:border-gray-700">
          <p className="text-xs text-muted-foreground dark:text-muted-foreground/70"><strong>Condizionali:</strong> <code className="text-muted-foreground/70">{'{{#if entry_fee}}...{{/if}}'}</code> &middot; <code className="text-muted-foreground/70">{'{{#if installments}}...{{/if}}'}</code> &middot; <code className="text-muted-foreground/70">{'{{#if installment_plan}}...{{/if}}'}</code></p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground dark:text-white font-semibold flex items-center gap-2"><Tag className="h-5 w-5" /> Categorie Prodotto</h3>
          <button onClick={() => setEditCategory({ name: '', description: '', color: 'blue', sort_order: 0 } as ProductCategory)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
            <Plus className="h-4 w-4" /> Nuova Categoria
          </button>
        </div>
        {productCategories.length === 0 ? (
          <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Nessuna categoria creata. Crea una categoria per organizzare i tuoi prodotti.</p>
        ) : (
          <div className="space-y-2">
            {productCategories.map((cat) => (
              <div key={cat.id} className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full bg-${cat.color || 'blue'}-500`} />
                  <div>
                    <span className="text-foreground dark:text-white font-medium">{cat.name}</span>
                    {cat.description && <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs mt-0.5">{cat.description}</p>}
                  </div>
                  {!cat.is_active && <Badge className="bg-muted/500/20 text-muted-foreground/70">Disattivata</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Tip label="Modifica"><button onClick={() => setEditCategory({ ...cat })} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white p-1"><Edit className="h-4 w-4" /></button></Tip>
                  <Tip label="Elimina"><button onClick={() => deleteCategory(cat.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button></Tip>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ModalWrapper open={!!editCategory} onClose={() => setEditCategory(null)} title={editCategory?.id ? 'Modifica Categoria' : 'Nuova Categoria'}>
        {editCategory && (
          <div className="space-y-4">
            <InputField label="Nome" value={editCategory.name} onChange={(v: string) => setEditCategory({ ...editCategory, name: v })} />
            <InputField label="Descrizione" value={editCategory.description || ''} onChange={(v: string) => setEditCategory({ ...editCategory, description: v })} />
            <div>
              <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Colore</label>
              <div className="flex gap-2">
                {categoryColors.map(c => (
                  <button key={c} type="button" onClick={() => setEditCategory({ ...editCategory, color: c })}
                    className={`w-8 h-8 rounded-full bg-${c}-500 border-2 transition-all ${editCategory.color === c ? 'border-white scale-110 ring-2 ring-blue-400' : 'border-transparent opacity-60 hover:opacity-100'}`} />
                ))}
              </div>
            </div>
            <InputField label="Ordine" value={String(editCategory.sort_order || 0)} onChange={(v: string) => setEditCategory({ ...editCategory, sort_order: parseInt(v) || 0 })} type="number" />
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditCategory(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
              <button onClick={saveCategory} disabled={savingCategory}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
                <Save className="h-4 w-4" />{savingCategory ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        )}
      </ModalWrapper>

      <ModalWrapper open={!!editTemplate} onClose={() => setEditTemplate(null)} title={editTemplate?.id ? 'Modifica Template' : 'Nuovo Template'} wide>
        {editTemplate && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Nome Template" value={editTemplate.name} onChange={(v: string) => setEditTemplate({ ...editTemplate, name: v })} />
              <InputField label="Descrizione" value={editTemplate.description} onChange={(v: string) => setEditTemplate({ ...editTemplate, description: v })} />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-muted-foreground dark:text-muted-foreground/70 cursor-pointer">
                <input type="checkbox" checked={editTemplate.is_default || false}
                  onChange={(e) => setEditTemplate({ ...editTemplate, is_default: e.target.checked })}
                  className="rounded border-border dark:border-gray-600" />
                Template predefinito
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Variabili</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {['company_name','company_address','company_zip','company_city','company_province','company_vat','company_cf','company_representative','master_company_name','master_vat','master_address','master_pec','master_phone','master_email','master_sdi','master_iban','master_fiscal_code','client_name','client_representative','client_vat','client_fiscal_code','client_address','client_zip','client_city','client_province','client_pec','client_sdi','client_email','client_phone','items_rows','subtotal_net','total_net','total_vat','total_gross','entry_fee','payment_formula','installments_count','first_installment_amount','installment_frequency','installment_plan_table','payment_terms','notes','contract_number','contract_date','contract_start_date','contract_end_date','contract_duration'].map(v => (
                  <button key={v} type="button"
                    onClick={() => {
                      const tag = `{{${v}}}`;
                      const ta = document.getElementById('template-html-textarea') as HTMLTextAreaElement;
                      if (ta) {
                        const start = ta.selectionStart;
                        const end = ta.selectionEnd;
                        const val = ta.value;
                        const newVal = val.substring(0, start) + tag + val.substring(end);
                        setEditTemplate((prev) => ({ ...(prev as ContractTemplate), html_content: newVal }));
                        setTimeout(() => { ta.focus(); ta.setSelectionRange(start + tag.length, start + tag.length); }, 50);
                      } else {
                        setEditTemplate((prev) => ({ ...(prev as ContractTemplate), html_content: ((prev as ContractTemplate).html_content || '') + tag }));
                      }
                    }}
                    className="px-2 py-1 text-xs rounded-md bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 border border-blue-500/20 font-mono transition-colors">
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Contenuto HTML</label>
              <textarea id="template-html-textarea" value={editTemplate.html_content || ''} rows={16}
                onChange={(e) => setEditTemplate({ ...editTemplate, html_content: e.target.value })}
                className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-inset focus:ring-blue-500 focus:border-transparent outline-none" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditTemplate(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
              <button onClick={saveTemplate} disabled={savingTemplate}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-foreground dark:text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
                <Save className="h-4 w-4" />{savingTemplate ? 'Salvataggio...' : 'Salva Template'}
              </button>
            </div>
          </div>
        )}
      </ModalWrapper>

      <ModalWrapper open={!!showTemplatePreview} onClose={() => setShowTemplatePreview(null)} title={`Anteprima: ${showTemplatePreview?.name || ''}`} wide>
        {showTemplatePreview && (
          <div className="border border-border dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="p-4 bg-white dark:bg-gray-800 prose prose-sm dark:prose-invert max-w-none max-h-[60vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: showTemplatePreview.html_content }} />
          </div>
        )}
      </ModalWrapper>
    </div>
  );
}

